<script lang="ts">
    import { onMount, getContext, tick } from 'svelte';
    import FutureStartNote from "./FutureStartNote.svelte";
    import { LocalDate } from "@/time";
    import { initForecastInputs } from "@/Util/api";
    import {baseRequest, debouncer, getCurrentFitnessSignature, getCurrentTrainingLoads, getDisplayWeight, getFunctionalWeight, getLTP, getRequest, specificityToRating, throttle, xssDetailsToSignature, type XSSDetails, durationToHoursMinutes, roundTo1Decimal, convertDisplayDistance, startDateFromInput } from '@/Util/utils';
    import route from '@/Util/routes';
    import {
        ProgramType, 
        specificityThreshold, 
        getFocus, 
        defaultRacePlan,
        eventReadinessBounds,
        defaultActivityChartOptions,
    } from "@/Common/constants";
    import type {
        XfaiFormProps,
    } from "@/Common/constants";
    import { DatePicker, SelectSlider, NumberInput, EditableText, InfoIcon } from "@/Common";
    import { ProgramFitnessSignature, ProgramTrainingLoad, XfaiXss, RaceSelector, RaceAchievabilityList } from './ProgramElements';
    import { createProgramAchievability } from './modules/ProgramAchievability';
    import { assessRace, capacityRates, createForecastInputs, type RaceForecastTargets } from './modules/ForecastFunctions';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import { processRecordsForChart } from '@/Util/XertAm5';
    import { getLibxert, getWorker } from '@/xert-wasm';
    import { addDays, differenceInCalendarDays } from 'date-fns';
    import type { MedianAndWeatherStores, MyFitnessStores } from '@/MyFitness/stores';
    import { writable } from 'svelte/store';
    import Am5Loader from '../Util/Am5Loader.svelte';
    import { AnalyticsController, type SummaryData } from '@/Activities/ActivityAnalytics';
    import XfaiGeneralSettings from './ProgramElements/XfaiGeneralSettings.svelte';
    import { WeeklyHoursSlider } from './ProgramElements/SetupFormInputs';
    import TrainPrefDayPicker from './ProgramElements/TrainPrefDayPicker.svelte';
    import RaceMapModal from './RaceMapModal.svelte'
    import { ToggleButtonSmall } from '@/Bootstrap';
    import { cn } from '@/lib/utils';
    import ActivityChartNew from '@/Activities/ActivityChartNew.svelte';
  import ActivityChart from '@/Activities/ActivityChart.svelte';
        
    const { userParams, trainingAdvice } = getContext<MyFitnessStores>('myFitness')
    const { trainingMedians } = getContext<MedianAndWeatherStores>('medianAndWeather')
    let currentSignature: XSSDetails
    let currentTL: XSSDetails
    
    function reactiveUpdate() {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
        if(initialInputs) updateInitialInputs() // Ensures it doesn't run on first load
    }
    // $: if($trainingAdvice) reactiveUpdate()
    $: if($trainingAdvice && !disabled) reactiveUpdate()

    //@ts-ignore
    let Xert = window.Xert; 
    
    type $$Props = XfaiFormProps<ProgramType.Race>
    export let plan = writable({...defaultRacePlan})
    export let forecastInputs: any
    export let disabled: boolean
    // Updating the existing plan can't move its start date, so the field is hidden then.
    export let hideStartDate = false
    export let achievabilityId = "race-achievability"
    export let forecastReady = false
    export const refresh = updateInitialInputs
    let className = ""
    export {className as class}
     
    /* Date input targetDate */
    let targetDate: string
    let startDate: string = LocalDate.format($plan.startDate ?? new Date())

    /* Race Control */
    let mpaData
    let analyzeRaceRunning = false

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const {currentFocusPower, focusPowerDuration} = focusPower

    /* Targets */
    let targetSignature: XSSDetails
    let targetTL: XSSDetails
    let targetXSSBreakdown: XSSDetails = { // Default to 0 so it loads in and doesn't cause page jumps
        xss: 0,
        xlss: 0,
        xhss: 0,
        xpss: 0,
    }
    let sfd: number
    let sp: number

    let targetSpeed = 0.0
    let averageSpeed = 0.0

    /* Achievability Module*/
    export const programAchievability  = createProgramAchievability(ProgramType.Race)
    const {overallAchievability, hoursAchievability, raceAchievability, targetAchievability} = programAchievability

    const setTargetWeightCurrent = () => $plan.userTargetWeight = getDisplayWeight($userParams.current_weight, $userParams.weight_unit)
    let chartAvgDataOverSeconds = 5
    
    /**
     * Responsible for setting the plan to the default values of the component
    */
    onMount(() => {
        // Setting the defaults for the page
        focusPower.updateFocusPower($plan.targetAthlete)
        $plan.userTargetWeight = $plan.userTargetWeight ? $plan.userTargetWeight : setTargetWeightCurrent()
        targetDate = LocalDate.format($plan.targetDate)
        updateInitialInputs() // Will only run if there are raceRecords
    })

    let initialInputs = null
    async function updateInitialInputs() {
        initialInputs = await initForecastInputs({
            start: $plan.targetDate,
            startDate: $plan.startDate,
            xss: $plan.xss,
            focus: $plan.targetAthlete,
            specificity_rating: $plan.specificity,
            mode: "target_xss",
        })
        debounceGetRaceTargets()
    }

    async function clearForecastInputs() {
        forecastInputs = null
        await tick() // await to ensure DOM updates
    }

    async function getRaceData() {
        if(!$plan.fileData && !$plan.activityPath) return

        const formData = $plan.fileData ?? new FormData()
        formData.append('path', $plan.activityPath)
        if ($plan.riderWeight) formData.append('rider_weight', getFunctionalWeight($plan.riderWeight, $userParams.weight_unit).toFixed())
        
        return window.fetch(
            route('ActivityController@createRaceData'),
            {
                ...baseRequest(),
                method: 'POST',
                body: formData,
            }, 
        )
        .then(response => {
            if(!response.ok) {
                return response.json().then(json => {
                    hideRaceSelector = false
                    throw new Error(json.user_message ?? 'Something went wrong.')
                })
            }
            return response.json()
        })
        .catch((error)=>{Xert.danger(error)})
    }

    let resultsStep = null
    async function getRaceTargets() {
        // First time request
        if(!$plan.raceRecords) {
            const raceData = await getRaceData()
            if(!raceData) return; // request failed
            $plan.name = raceData.name
            $plan.riderWeight = getDisplayWeight(raceData.rider_weight, $userParams.weight_unit)
            $plan.raceRecords = {
                ...raceData.records,
                originalPower: raceData.records.power,
                originalTime: raceData.records.time
            }
            averageSpeed = 0
            targetSpeed = 0
        }

        // Transform power

        let speedScale = 1.0;
        if (averageSpeed > 0 && targetSpeed > 0) {
            speedScale = averageSpeed / targetSpeed
        }
        
        let output = (await getLibxert()).transform_power($plan.raceRecords.originalTime, $plan.raceRecords.alt, $plan.raceRecords.dist, $plan.raceRecords.originalPower, Number(getFunctionalWeight($plan.riderWeight, $userParams.weight_unit)), Number(getFunctionalWeight($plan.userTargetWeight, $userParams.weight_unit)), Number(speedScale))
        let transformedPower = []
        let transformedTime = []
        let powerScale = output.powerScale
        if (averageSpeed > 0) {
            $plan.raceSpeedScale = output.speedScale
            $plan.racePowerScale = output.powerScale
        }
        $plan.raceRecords.time = output.ts
        $plan.raceRecords.power = output.ps

        // Get targets (FS & TL) from wasm.
        const rampRates = programAchievability.getRampRates()
        const weeksUntilTarget = differenceInCalendarDays($plan.targetDate, new Date(initialInputs.t0*1000))/7.0
        // The rider's own weekly build rates, which weigh the systems in the loads the race needs (ready_*). The inputs'
        // target loads don't matter to them: today's.
        const riderRates = await capacityRates(getForecastInputs(currentTL, {xss: 0, xlss: 0, xhss: 0, xpss: 0}), $plan.cal_settings, $trainingMedians)
        const results = await (getWorker()).analyze_race(
            $plan.raceRecords.time, $plan.raceRecords.dist, $plan.raceRecords.power, 
            $userParams.ir_params, $trainingAdvice.at_state.tl, $trainingAdvice.signature, rampRates, weeksUntilTarget, 
            $plan.settings.taper, $plan.ignoreDifficulty ? 9999 : $plan.difficultyScore, $plan.ignoreCarbs ? 9999 :$plan.carbsPerHour, $userParams.metric,
            riderRates,
        )
        console.log("analyze race results", results)

        // Set targets: the loads the race needs, each system raised only as far as it must be (ready_*), rather than
        // the loads analyze_race's ramp rates reach by the target date, which take a system the race hardly depends on
        // past what the plan's days can build. Its targets when it doesn't give them.
        const ready = results.step?.ready_ltl != null ? results.step : null
        targetTL = ready ? {
                xlss: ready.ready_ltl,
                xhss: ready.ready_htl,
                xpss: ready.ready_ptl,
                xss: ready.ready_ltl + ready.ready_htl + ready.ready_ptl,
        } : {
                xlss: results.ltl,
                xhss: results.htl,
                xpss: results.ptl,
                xss: results.ltl + results.htl + results.ptl,
        }
        targetSignature = {
                xlss: ready ? ready.ready_ftp : results.ftp,
                xhss: ready ? ready.ready_atc : results.atc,
                xpss: ready ? ready.ready_pp : results.pp,
                xss: null
        }
        targetXSSBreakdown = ready ? {
            xss: ready.ready_xss,
            xlss: ready.ready_xlss,
            xhss: ready.ready_xhss,
            xpss: ready.ready_xpss,
        } : {
            xss: results.xss,
            xlss: results.xlss,
            xhss: results.xhss,
            xpss: results.xpss,
        }
        $plan.xss = targetXSSBreakdown.xss

        resultsStep = results.step
        refreshChartMpaData()
    }

    const refreshChartMpaData = async () => {
        if (!targetSignature) return

        if (!$plan.raceRecords) {
            // Most likely change race was called while analyze race was running => This call has been abandoned
            console.warn('attempted chart refresh without race records')
            return
        }

        // Get mpa data for the chart
        mpaData = await processRecordsForChart(
            $plan.raceRecords, 
            {   
                ftp: targetSignature.xlss,
                atc: targetSignature.xhss,
                pp: targetSignature.xpss,
                ltp: getLTP(targetSignature.xlss, targetSignature.xhss),
                carb_bias: $userParams.carb_bias,
            }, 
            $userParams.metric,
            chartAvgDataOverSeconds
        )
        
        // Focus
        sfd = mpaData.summary.sfd
        $plan.targetAthlete = getFocus($userParams.athletes, sfd)
        focusPower.updateFocusPower($plan.targetAthlete)

        // Specificity
        sp = mpaData.summary.sp
        $plan.specificity = specificityToRating(sp)

        // Moving time
        $plan.duration = (mpaData.summary.moving_time.adu ?? mpaData.summary.adu) / 60.0

        if (averageSpeed == 0 && mpaData.summary.avg_spd > 0) {
            averageSpeed = roundTo1Decimal(convertDisplayDistance(mpaData.summary.avg_spd, $userParams.activityUnits.dist));
            if ($plan.raceSpeedScale != 1.0) {
                targetSpeed = roundTo1Decimal(convertDisplayDistance(mpaData.summary.avg_spd, $userParams.activityUnits.dist) / ($plan.raceSpeedScale ?? 1.0))
                debounceGetRaceTargets()
            } else {
                targetSpeed = averageSpeed
            }
        }

        // Update inputs and validate
        forecastInputs = {...getForecastInputs()}
        programAchievability.validateRace(resultsStep, $plan.targetDate, new Date(initialInputs.t0*1000), targetTL, currentTL, $plan.settings.maxWeeklyHours)
        updateReadiness(forecastInputs)
    }

    // The race's ready date against what the plan's availability and settings can build (assessRace): the target loads,
    // the loads the race needs. When the days don't build them by the target date, the date is the earliest they do;
    // otherwise analyze_race's stands. Each assessment supersedes the ones before it.
    let readinessRequest = 0
    async function updateReadiness(inputs: any) {
        if (!resultsStep?.ready_ltl || !inputs) return
        const request = ++readinessRequest
        const assessment = await assessRace(inputs, $plan.cal_settings, $trainingMedians, "race")
        if (request !== readinessRequest || !assessment || assessment.reachable) return
        programAchievability.setReadyBy(assessment.earliestDate ?? "never")
    }

    let throttleRace = throttle()
    let debounceRaceInputsChange = debouncer(500)
    async function debounceGetRaceTargets() {
        await clearForecastInputs()
        analyzeRaceRunning = true
        debounceRaceInputsChange.debounce(throttleGetRaceTargets)
        // In theory this risks a race condition, but since debounces are at least 500ms apart we should be safe
    }
    async function throttleGetRaceTargets() {
        throttleRace.throttle(getRaceTargets)
        await throttleRace.wait()
        analyzeRaceRunning = false
    }

    function getForecastInputs(loads: XSSDetails = targetTL, raceXss: XSSDetails = targetXSSBreakdown) {
        return createForecastInputs<RaceForecastTargets>(
            {   
                program: ProgramType.Race,
                xss: $plan.xss,
                focus: $plan.targetAthlete,
                specificity_rating: $plan.specificity,
                start: $plan.targetDate,
                currentFocusPower: $currentFocusPower,
                ltl_target: loads.xlss,
                htl_target: loads.xhss,
                ptl_target: loads.xpss,
                xlss: raceXss.xlss,
                xhss: raceXss.xhss,
                xpss: raceXss.xpss, 
                sfd: sfd,
                sp: sp,
            },
            $userParams.athletes,
            initialInputs,
            $plan.settings
        )
    }

    /**
     * When changing settings or availability validation needs to be rerun, and the targets, which depend on the rider's
     * weekly build rates (see getRaceTargets), found again.
     */
     async function refreshForecastInputs() {
        if ($plan.raceRecords) return debounceGetRaceTargets()
        await clearForecastInputs()
        forecastInputs = getForecastInputs()
        programAchievability.validateRace(resultsStep, $plan.targetDate, new Date(initialInputs.t0*1000), targetTL, currentTL, $plan.settings.maxWeeklyHours)
        updateReadiness(forecastInputs)
    }

    let hideRaceSelector = false
    const changeRace = () => {
        allowShowForm = false
        $plan.raceRecords = null
        $plan.activityPath = null
        $plan.riderWeight = 0
        $plan.fileData = null
        targetTL = null
        targetSignature = null
        mpaData = null
        initialInputs = null
        clearForecastInputs()
    }

    let debounceRefreshChartMpaData = debouncer(500)

    let showActivityMap: (..._) => void

    let summary: Partial<SummaryData> = {}
    const onUpdateSummary = (summaryUpdates) => {
        summary = {
            ...summary,
            ...summaryUpdates
        }
    }
    const raceAnalytics = new AnalyticsController(onUpdateSummary)
    const afterZoom = (startIndex: number, endIndex: number, data: [any]) => {
        raceAnalytics.processMpaDataSelection(data, startIndex, endIndex, xssDetailsToSignature(targetSignature))
    }


    let allowShowForm = !!$plan.raceRecords
    let formShown: boolean
    $: {
        formShown = allowShowForm && !!$plan.raceRecords
        forecastReady = formShown
    }
</script>

<div class="flex justify-center" class:hidden={formShown}>
    <RaceSelector class={cn("max-w-5xl", className)}
        bind:riderWeight={$plan.riderWeight} bind:fileData={$plan.fileData} bind:raceLogoUrl={$plan.raceLogoUrl}
        bind:activityPath={$plan.activityPath} bind:raceDate={targetDate}
        {plan} {mpaData} {targetSignature} {programAchievability}
        on:changeRace={changeRace}
        on:select={() => {
            allowShowForm = true

            if ($plan.raceRecords) return // This line runs when select fires after an assess... specific to race library's flow

            $plan.targetDate = LocalDate.atNoon(targetDate)
            updateInitialInputs()
        }}
        on:assess={() => {
            setTargetWeightCurrent()
            $plan.targetDate = LocalDate.atNoon(targetDate)
            $plan = { // Ensures difficulty and carbs are always ignored for assess
                ...$plan,
                ignoreCarbs: defaultRacePlan.ignoreCarbs,
                ignoreDifficulty: defaultRacePlan.ignoreDifficulty,
                difficultyScore: defaultRacePlan.difficultyScore,
                carbsPerHour: defaultRacePlan.carbsPerHour,
            }
            updateInitialInputs()
        }}
        on:optionChange={() => {
            changeRace()
            setTargetWeightCurrent() 
            $plan.raceRecords = null
        }}
    />
</div>

<div class={className} class:hidden={!formShown}>
    <div class="flex flex-col gap-8 items-center">
        <div class="grid md:grid-cols-[1fr,auto,1fr] w-full items-center">
            <EditableText class="md:col-start-2 text-xl font-bold text-center" bind:text={$plan.name} />
            <button class="ml-4 col-start-2 md:col-start-3 justify-self-end btn-xfai-sm border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue] text-sm" on:click={changeRace}>Change race</button>
        </div>
        <div class="grid gap-y-4 md:grid-cols-[2fr,min-content,3fr] xl:grid-cols-[3fr,min-content,5fr,min-content,2fr] md:gap-x-5 md:gap-y-3 w-full" >
            <span class="text-lg text-left mb-2">Target Information <InfoIcon href="http://baronbiosys.com/blog/race-ai-setup-guide"/></span>
            <div class="md:row-start-2 grid grid-cols-[2fr,5fr] auto-rows-max items-start gap-y-8 gap-x-3 md:gap-x-6 md:text-right text-left text-base md:grid">

                <slot name="adapt-choice"/>
                
            {#if !hideStartDate}
            <span class="text-slate-600">Start Date</span>
                <DatePicker class="w-52 text-base" min={LocalDate.format(new Date())} max={LocalDate.format(addDays($plan.targetDate, -1))} {disabled}
                    bind:value={startDate}
                    on:change={ () => {
                        $plan.startDate = startDateFromInput(startDate)
                        updateInitialInputs()
                    }}
                />
                <FutureStartNote startDate={$plan.startDate} />
            {/if}

                <span class="text-slate-600">End Date</span>
                <DatePicker class="w-52 text-base" min={LocalDate.format(addDays($plan.startDate ?? new Date(), 1))} disabled={disabled}
                    bind:value={targetDate} 
                    on:change={() => {
                        $plan.targetDate = LocalDate.atNoon(targetDate)
                        updateInitialInputs()
                    }}
                />

                <span class="text-slate-600">Average Speed</span>
                <div class="grid grid-cols-[auto,31px] gap-y-3 gap-x-4 items-center">
                    <NumberInput min={5.0} max={40.0} step={0.1} unit={$userParams.activityUnits.spd} disabled={disabled || averageSpeed <= 0}
                        bind:value={targetSpeed} 
                        on:change={debounceGetRaceTargets} 
                    />

                    {#if targetSpeed != averageSpeed}
                    <button on:click={()=>{ targetSpeed = roundTo1Decimal(averageSpeed); debounceGetRaceTargets(); }} class='btn-xfai-sm ml-auto border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue] text-sm fa fa-lg fa-light fa-undo' title="Reset Average Speed">
                    </button>
                    {/if}
                </div>
                <span class="text-slate-600">Event Duration</span>
                <div class="grid grid-cols-[auto,31px] gap-y-3 gap-x-4 items-center">
                    <span data-enabled={true} class="text-base data-[enabled=false]:text-slate-300">
                        {#if $plan.duration > 0}
                        {durationToHoursMinutes($plan.duration)}
                        {/if}
                    </span>
                </div>

                <span class="text-slate-600">Max Weekly Hours</span>
                <WeeklyHoursSlider {disabled} hoursAchievability={$hoursAchievability.status}
                    bind:hours={$plan.settings.maxWeeklyHours}
                    onInput={refreshForecastInputs}
                />

                <span class="text-slate-600">Difficulty Score <InfoIcon href="http://baronbiosys.com/glossary/race-ai-difficulty"/></span>
                <div class="grid grid-cols-[auto,31px] gap-y-3 gap-x-4 items-center">
                    <SelectSlider min={60} max={200} step={1} disabled={disabled || $plan.ignoreDifficulty}
                        bind:value={$plan.difficultyScore}
                        on:change={debounceGetRaceTargets}
                    /> 
                    <span data-enabled={!$plan.ignoreDifficulty} class="text-base data-[enabled=false]:text-slate-300">{$plan.ignoreDifficulty ? 200 : $plan.difficultyScore}</span>
                    <ToggleButtonSmall class="col-span-2 justify-self-start self-start text-sm text-slate-500" label={"Unrestricted"}
                        bind:isChecked={$plan.ignoreDifficulty} 
                        on:change={() => {
                            debounceGetRaceTargets()
                        }}
                    />
                </div>

                <span class="text-slate-600">Carbs per Hour <InfoIcon href="http://baronbiosys.com/glossary/race-ai-carb-rate"/></span>
                <div class="grid grid-cols-[auto,31px] gap-y-3 gap-x-4 items-center">
                    <SelectSlider min={30} max={150} step={1} disabled={disabled || $plan.ignoreCarbs}
                        bind:value={$plan.carbsPerHour} 
                        on:change={debounceGetRaceTargets} 
                    />
                    <span data-enabled={!$plan.ignoreCarbs} class="text-base data-[enabled=false]:text-slate-300">{$plan.ignoreCarbs ? 150 : $plan.carbsPerHour}</span>
                    <ToggleButtonSmall class="col-span-2 justify-self-start self-start text-sm text-slate-500" label={"Unrestricted"}
                        bind:isChecked={$plan.ignoreCarbs} 
                        on:change={() => {
                            debounceGetRaceTargets()
                        }}
                    />
                </div>

                <span class="text-slate-600">Event Readiness <InfoIcon href="http://baronbiosys.com/glossary/event-readiness"/></span>
                <NumberInput {...eventReadinessBounds} class="max-w-[8rem]" {disabled}
                    bind:value={$plan.settings.taper}
                    on:change={debounceGetRaceTargets} 
                />

                <span class="text-slate-600">Target Weight</span>
                <NumberInput min={1} max={500} step={1} class="max-w-[8rem]" unit={$userParams.weight_unit} {disabled}
                    bind:value={$plan.userTargetWeight} 
                    on:change={debounceGetRaceTargets} 
                />
                
                <TrainPrefDayPicker class='col-span-2' bind:cal_settings={$plan.cal_settings} onSubmit={refreshForecastInputs}/>

                <XfaiGeneralSettings class="col-span-2" bind:settings={$plan.settings} {disabled} onInput={refreshForecastInputs} />

            </div>

            <div class="md:row-start-2 h-px md:w-px md:h-auto bg-black/10"/>

            <span class="md:col-start-3 text-lg mb-2">Estimated Training Needs</span>
            <div class="md:row-start-2 flex flex-col gap-8">
                <div class="grid gap-x-4 gap-y-4 items-start auto-cols-max text-base leading-none">

                    <span class="row-start-1 text-sm text-slate-500">Focus</span> 
                    <span class="font-semibold">
                        {$plan.targetAthlete}
                        <div class="text-sm leading-none text-slate-600 font-normal">
                            {$focusPowerDuration} minute power
                        </div>
                    </span>

                    <span class="row-start-2 text-sm text-slate-500">Specificity</span>
                    <span class="row-start-2 font-semibold">
                        {$plan.specificity}
                        <span class="text-slate-600 font-normal text-sm leading-none">
                            {specificityThreshold[$plan.specificity]}
                        </span>
                    </span>

                    <span class="row-start-3 text-sm text-left text-slate-500">XSS</span>
                    <XfaiXss class="border-dashed border rounded px-3 py-2 border-slate-600" xss={targetXSSBreakdown} />

                </div>
                
                <ProgramTrainingLoad 
                    targetAchievability={$targetAchievability} {currentTL} {targetTL} disabled={analyzeRaceRunning}
                />
                <ProgramFitnessSignature 
                    targetAchievability={$targetAchievability} {currentSignature} {targetSignature} disabled={analyzeRaceRunning}
                />
                
                <div class="flex flex-col text-base">
                    <div class="flex">
                        <span class="font-bold">MPA Chart</span>
                        <button on:click={showActivityMap} class='btn-xfai-sm ml-auto border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue] text-sm'>
                            Show Details
                        </button>
                    </div>
                    <Am5Loader>
                        {#if mpaData}
                        <ActivityChartNew 
                            class={cn("h-64", "pointer-events-none", analyzeRaceRunning && "hidden")}
                            {mpaData}
                            seriesVisibility={{
                                difficulty: "startHidden",
                                heartRate: "off",
                                targetPower: "off",
                                speed: "startHidden",
                                cadence: "startHidden",
                            }}
                            showLegend={false}
                            showScrollbar={false}
                        />
                        {/if}
                    </Am5Loader>
                    {#if !mpaData || analyzeRaceRunning}
                        <div class="text-center">
                            <i class="fa-solid fa-spin fa-circle-notch text-5xl text-xerteal-700"/>
                        </div>
                    {/if}
                    
                </div>
            </div>

            <div class="hidden h-px md:row-start-3 md:col-span-3 xl:block xl:col-span-1 xl:row-start-2 xl:w-px xl:h-auto bg-black/10"/>
            
            <span id={achievabilityId} class="md:row-start-4 md:col-span-3 xl:row-start-1 xl:col-span-1 xl:col-start-5 text-lg mb-2">Achievability</span>
            <div class="md:row-start-5 md:col-span-3 xl:col-span-1 xl:row-start-2">
                <RaceAchievabilityList 
                    {programAchievability}
                    {analyzeRaceRunning}
                    plan={$plan}
                    targetDate={$plan.targetDate}
                    on:changeDate={() => {
                        if($raceAchievability.readyBy instanceof Date) {
                            $plan.targetDate = $raceAchievability.readyBy
                            targetDate = LocalDate.format($plan.targetDate)
                            updateInitialInputs()
                        }
                    }}
                />
            </div>
        </div>
        
    </div>
</div>

{#if formShown}
    <RaceMapModal
        bind:show={showActivityMap} 
        bind:chartAvgDataOverSeconds 
        onSliderInput={() => debounceRefreshChartMpaData.debounce(refreshChartMpaData)}
        {mpaData} {summary} {afterZoom}
        mapPolyline={$plan.raceRecords.polyline} mapUrl={`/activities/${$plan.activityPath}/map`}
    />
{/if}